import invariant from 'invariant';
import { isEmpty, isFunction, isString, conformsTo } from 'lodash';

import checkStore from './checkStore';
import { DAEMON, ONCE_TILL_UNMOUNT, RESTART_ON_REMOUNT } from './constants';

const allowedModes = [RESTART_ON_REMOUNT, DAEMON, ONCE_TILL_UNMOUNT];
const PRODUCTION = 'production';

const checkKey = (key) =>
  invariant(
    isString(key) && !isEmpty(key),
    '(app/utils...) injectSaga: Expected `key` to be a non empty string'
  );

const checkDescriptor = (descriptor) => {
  const shape = {
    saga: isFunction,
    mode: (mode) => isString(mode) && allowedModes.includes(mode)
  };
  invariant(
    conformsTo(descriptor, shape),
    '(app/utils...) injectSaga: Expected a valid saga descriptor'
  );
};

// eslint-disable-next-line default-param-last
export const injectSagaFactory =
  (store) =>
  (key, descriptor = {}, args) => {
    const newDescriptor = {
      ...descriptor,
      mode: descriptor.mode || RESTART_ON_REMOUNT
    };
    const { saga, mode } = newDescriptor;

    checkKey(key);
    checkDescriptor(newDescriptor);

    let hasSaga = Reflect.has(store.injectedSagas, key);

    if (process.env.NODE_ENV !== PRODUCTION) {
      const oldDescriptor = store.injectedSagas[key];
      // enable hot reloading of daemon and once-till-unmount sagas
      if (hasSaga && oldDescriptor.saga !== saga) {
        oldDescriptor.task.cancel();
        hasSaga = false;
      }
    }

    if (!hasSaga || (mode !== DAEMON && mode !== ONCE_TILL_UNMOUNT)) {
      /* eslint-disable no-param-reassign */
      store.injectedSagas[key] = {
        ...newDescriptor,
        task: store.runSaga(saga, args)
      };
      /* eslint-enable no-param-reassign */
    }
  };

export const ejectSagaFactory = (store) => (key) => {
  checkKey(key);

  if (Reflect.has(store.injectedSagas, key)) {
    const descriptor = store.injectedSagas[key];
    if (descriptor.mode && descriptor.mode !== DAEMON) {
      descriptor.task.cancel();
      // Clean up in production; in development we need `descriptor.saga` for hot reloading
      if (process.env.NODE_ENV === PRODUCTION) {
        // Need some value to be able to detect `ONCE_TILL_UNMOUNT` sagas in `injectSaga`
        store.injectedSagas[key] = 'done'; // eslint-disable-line no-param-reassign
      }
    }
  }
};

const getInjectors = (store) => {
  checkStore(store);

  return {
    injectSaga: injectSagaFactory(store),
    ejectSaga: ejectSagaFactory(store)
  };
};

export default getInjectors;
